import { alpha, Box, Chip, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { SubNavAppBar } from "components/AppBar";
import { Link, matchPath, useLocation, useParams } from "react-router-dom";

import { ADDRESS_SECTION_ID, ADDRESS_SECTION_ICON } from "./AddressSection";
import { BASICS_SECTION_ID, BASICS_SECTION_ICON } from "./BasicsSection";
import {
  FACILITY_CONTACTS_SECTION_ID,
  FACILITY_CONTACTS_SECTION_ICON,
} from "./FacilityContactsSection";
import {
  FACILITY_CREDENTIALS_SECTION_ID,
  FACILITY_CREDENTIALS_SECTION_ICON,
} from "./FacilityCredentialsSection";
import {
  FACILITY_DOCUMENTS_SECTION_ID,
  FACILITY_DOCUMENTS_SECTION_ICON,
} from "./FacilityDocumentsSection";
import { LOCAL_AGENCIES_SECTION_ID, LOCAL_AGENCIES_SECTION_ICON } from "./LocalAgenciesSection";
import {
  STATE_INFORMATION_SECTION_ID,
  STATE_INFORMATION_SECTION_ICON,
} from "./StateInformationSection";

interface SectionLink {
  id: string;
  label: string;
  icon: string;
}

const sections: SectionLink[] = [
  { id: BASICS_SECTION_ID, label: "Basics", icon: BASICS_SECTION_ICON },
  { id: ADDRESS_SECTION_ID, label: "Address", icon: ADDRESS_SECTION_ICON },
  { id: LOCAL_AGENCIES_SECTION_ID, label: "Local Agencies", icon: LOCAL_AGENCIES_SECTION_ICON },
  {
    id: STATE_INFORMATION_SECTION_ID,
    label: "State Information",
    icon: STATE_INFORMATION_SECTION_ICON,
  },
  {
    id: FACILITY_CONTACTS_SECTION_ID,
    label: "Facility Contacts",
    icon: FACILITY_CONTACTS_SECTION_ICON,
  },
  {
    id: FACILITY_DOCUMENTS_SECTION_ID,
    label: "Facility Documents",
    icon: FACILITY_DOCUMENTS_SECTION_ICON,
  },
  {
    id: FACILITY_CREDENTIALS_SECTION_ID,
    label: "Credentials",
    icon: FACILITY_CREDENTIALS_SECTION_ICON,
  },
];

export const SECTION_NAV_ID = "section-nav";

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  const nav = document.getElementById(SECTION_NAV_ID);
  if (element && nav) {
    const navBottom = nav.getBoundingClientRect().bottom;
    const elementTop = element.getBoundingClientRect().top;
    const scrollBy = elementTop - navBottom - 16;
    window.scrollBy({ top: scrollBy, behavior: "smooth" });
  }
};

export const FacilityHeaderV2: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { pathname } = useLocation();

  const basePath = `/o/${tenantId}/facilities/${facilityId}`;
  const tabs = [
    { label: "Facility Overview", to: `${basePath}/profile` },
    { label: "Tasks", to: `${basePath}/tasks` },
  ];

  const activeTabIndex = tabs.findIndex(({ to }) => matchPath(`${to}/*`, pathname));
  const isProfileTab = activeTabIndex === 0;

  return (
    <SubNavAppBar id={SECTION_NAV_ID}>
      <Tabs
        value={activeTabIndex >= 0 ? activeTabIndex : 0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} component={Link} to={tab.to} sx={{ px: 3 }} />
        ))}
      </Tabs>
      {isProfileTab && (
        <Box px={3} py={1.5}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Sections
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {sections.map((section) => (
                <Chip
                  key={section.id}
                  label={`${section.icon} ${section.label}`}
                  onClick={() => scrollToSection(section.id)}
                  size="small"
                  sx={{
                    backgroundColor: alpha(
                      theme.palette.info.main,
                      theme.palette.action.selectedOpacity
                    ),
                    color: theme.palette.text.primary,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      )}
    </SubNavAppBar>
  );
};
